import { Marble } from './marble';
import { initialZoom, Skills, Themes, zoomThreshold } from './data/constants';
import { ParticleManager } from './particleManager';
import { StageDef, stages } from './data/maps';
import { parseName, shuffle } from './utils/utils';
import { Camera } from './camera';
import { RouletteRenderer } from './rouletteRenderer';
import { SkillEffect } from './skillEffect';
import { GameObject } from './gameObject';
import options from './options';
import { bound } from './utils/bound.decorator';
import { UIObject } from './UIObject';
import { RankRenderer } from './rankRenderer';
import { Minimap } from './minimap';
import { VideoRecorder } from './utils/videoRecorder';
import { IPhysics } from './IPhysics';
import { Box2dPhysics } from './physics-box2d';
import { MouseEventHandlerName, MouseEventName } from './types/mouseEvents.type';
import { FastForwader } from './fastForwader';
import { ColorTheme } from './types/ColorTheme';

export class Roulette extends EventTarget {
  private _marbles: Marble[] = [];
  private _allMarbles: Marble[] = []; // 순위 리스트 표시를 위한 전체 구슬 목록
  private _finishedMarbles: Marble[] = []; // 골을 통과한 구슬 목록 (순위 리스트용)

  private _lastTime: number = 0;
  private _elapsed: number = 0;

  private _updateInterval = 10;
  private _timeScale = 1;
  private _speed = 1;

  private _winners: Marble[] = [];
  private _particleManager = new ParticleManager();
  private _stage: StageDef | null = null;

  private _camera: Camera = new Camera();
  private _renderer: RouletteRenderer = new RouletteRenderer();

  private _effects: GameObject[] = [];

  private _winnerRank = 0;
  private _winnerCount = 1;
  private _totalMarbleCount = 0;
  private _goalDist: number = Infinity;
  private _isRunning: boolean = false;
  private _winner: Marble | null = null;

  private _uiObjects: UIObject[] = [];

  private _autoRecording: boolean = false;
  private _recorder!: VideoRecorder;

  private physics!: IPhysics;

  private _isReady: boolean = false;
  private fastForwarder!: FastForwader;
  private _theme: ColorTheme = Themes.dark;

  get isReady() {
    return this._isReady;
  }

  constructor() {
    super();
    this._renderer.init().then(() => {
      this._init().then(() => {
        this._isReady = true;
        this._update();
      });
    });
  }

  public getZoom() {
    return initialZoom * this._camera.zoom;
  }

  private addUiObject(obj: UIObject) {
    this._uiObjects.push(obj);
    if (obj.onWheel) {
      this._renderer.canvas.addEventListener('wheel', obj.onWheel);
    }
    if (obj.onMessage) {
      obj.onMessage((msg) => {
        console.log('onMessage', msg);
        this.dispatchEvent(new CustomEvent('message', { detail: msg }));
      });
    }
  }

  @bound
  private _update() {
    if (!this._lastTime) this._lastTime = Date.now();
    const currentTime = Date.now();

    this._elapsed += (currentTime - this._lastTime) * this._speed * this.fastForwarder.speed;
    if (this._elapsed > 100) {
      this._elapsed %= 100;
    }
    this._lastTime = currentTime;

    const interval = (this._updateInterval / 1000) * this._timeScale;

    while (this._elapsed >= this._updateInterval) {
      this.physics.step(interval);
      this._updateMarbles(this._updateInterval);
      this._particleManager.update(this._updateInterval);
      this._updateEffects(this._updateInterval);
      this._elapsed -= this._updateInterval;
      this._uiObjects.forEach((obj) => obj.update(this._updateInterval));
    }

    if (this._marbles.length > 1) {
      this._marbles.sort((a, b) => b.y - a.y);
    }

    if (this._stage) {
      this._camera.update({
        marbles: this._marbles,
        stage: this._stage,
        needToZoom: this._goalDist < zoomThreshold,
        targetIndex:
          this._winners.length > 0
            ? this._winnerRank - this._winners.length
            : 0,
      });
    }

    this._render();
    window.requestAnimationFrame(this._update);
  }

  private _updateMarbles(deltaTime: number) {
    if (!this._stage) return;

    for (let i = 0; i < this._marbles.length; i++) {
      const marble = this._marbles[i];
      marble.update(deltaTime);
      if (marble.skill === Skills.Impact) {
        this._effects.push(new SkillEffect(marble.x, marble.y));
        this.physics.impact(marble.id);
      }
      if (marble.y > this._stage.goalY) {
        // 골을 통과한 구슬을 finishedMarbles에 추가 (순위 리스트 표시용)
        if (!this._finishedMarbles.some(m => m.id === marble.id)) {
          this._finishedMarbles.push(marble);
        }
        
        // 모든 골을 통과한 구슬을 winners에 추가 (중복 체크 없이)
        this._winners.push(marble);
        
        // 중복을 제외한 고유한 승자 수 확인 (게임 종료 판정용)
        const uniqueWinners = this._winners.filter((w, index, self) => 
          index === self.findIndex(winner => winner.name === w.name)
        );
        
        // 당첨 인원 수만큼 도달했는지 확인
        if (this._isRunning && uniqueWinners.length >= this._winnerCount) {
          // 승자 이름들 생성 (중복 제외, 표시용)
          const winnerNames = uniqueWinners
            .slice(0, this._winnerCount)
            .map(w => w.name);
          const winnerText = winnerNames.length === 1 
            ? winnerNames[0]
            : winnerNames.join(' & ');
          
          this.dispatchEvent(
            new CustomEvent('goal', { 
              detail: { 
                winner: winnerText,
                winners: winnerNames
              } 
            }),
          );
          this._winner = uniqueWinners[this._winnerCount - 1];
          this._isRunning = false;
          this._particleManager.shot(
            this._renderer.width,
            this._renderer.height,
          );
          setTimeout(() => {
            this._recorder.stop();
          }, 1000);
        }
        
        setTimeout(() => {
          this.physics.removeMarble(marble.id);
        }, 500);
      }
    }

    const targetIndex = this._winnerRank - this._winners.length;
    // 골을 통과한 구슬은 _marbles에서 제거 (화면에서 사라짐)
    this._marbles = this._marbles.filter(
      (marble) => marble.y <= this._stage!.goalY,
    );
    const topY = this._marbles[targetIndex] ? this._marbles[targetIndex].y : 0;
    this._goalDist = Math.abs(this._stage.zoomY - topY);
    this._timeScale = this._calcTimeScale();
  }

  private _calcTimeScale(): number {
    if (!this._stage) return 1;
    const targetIndex = this._winnerRank - this._winners.length;
    if (
      this._winners.length < this._winnerRank + 1 &&
      this._goalDist < zoomThreshold
    ) {
      if (
        this._marbles[targetIndex] &&
        this._marbles[targetIndex].y >
        this._stage.zoomY - zoomThreshold * 1.2 &&
        (this._marbles[targetIndex - 1] || this._marbles[targetIndex + 1])
      ) {
        return Math.max(0.2, this._goalDist / zoomThreshold);
      }
    }
    return 1;
  }

  private _updateEffects(deltaTime: number) {
    this._effects.forEach((effect) => effect.update(deltaTime));
    this._effects = this._effects.filter((effect) => !effect.isDestroy);
  }

  private _render() {
    if (!this._stage) return;
    // 순위 리스트용: 골을 통과한 구슬 + 아직 골을 통과하지 않은 구슬
    // winners는 별도로 표시되므로, winners에 포함되지 않은 구슬들만 추출
    // 모든 구슬을 표시하되, winners에 포함된 구슬은 제외
    const allMarblesForRank = [
      ...this._finishedMarbles.filter(m => !this._winners.some(w => w.id === m.id)),
      ...this._marbles
    ].sort((a, b) => {
      // 골을 통과한 구슬이 먼저 (y가 큰 것부터)
      const aFinished = this._finishedMarbles.some(m => m.id === a.id);
      const bFinished = this._finishedMarbles.some(m => m.id === b.id);
      if (aFinished && !bFinished) return -1;
      if (!aFinished && bFinished) return 1;
      return b.y - a.y;
    });
    
    const renderParams = {
      camera: this._camera,
      stage: this._stage,
      entities: this.physics.getEntities(),
      marbles: this._marbles, // 화면 렌더링용: 골을 통과하지 않은 구슬만
      allMarbles: allMarblesForRank, // 순위 리스트용: 전체 구슬
      winners: this._winners, // 모든 winners (중복 포함, renderWinner에서 필터링)
      particleManager: this._particleManager,
      effects: this._effects,
      winnerRank: this._winnerRank,
      winner: this._winner,
      winnerCount: this._winnerCount,
      size: { x: this._renderer.width, y: this._renderer.height },
      theme: this._theme,
    };
    this._renderer.render(renderParams, this._uiObjects);
  }

  private async _init() {
    this._recorder = new VideoRecorder(this._renderer.canvas);

    this.physics = new Box2dPhysics();
    await this.physics.init();

    this.addUiObject(new RankRenderer());
    this.attachEvent();
    const minimap = new Minimap();
    minimap.onViewportChange((pos) => {
      if (pos) {
        this._camera.setPosition(pos, false);
        this._camera.lock(true);
      } else {
        this._camera.lock(false);
      }
    });
    this.addUiObject(minimap);
    this.fastForwarder = new FastForwader();
    this.addUiObject(this.fastForwarder);
    this._stage = stages[0];
    this._loadMap();
  }

  @bound
  private mouseHandler(eventName: MouseEventName, e: MouseEvent) {
    const handlerName = `on${eventName}` as MouseEventHandlerName;

    const sizeFactor = this._renderer.sizeFactor;
    const pos = { x: e.offsetX * sizeFactor, y: e.offsetY * sizeFactor };
    this._uiObjects.forEach((obj) => {
      if (!obj[handlerName]) return;
      const bounds = obj.getBoundingBox();
      if (!bounds) {
        obj[handlerName]({ ...pos, button: e.button });
      } else if (
        bounds &&
        pos.x >= bounds.x &&
        pos.y >= bounds.y &&
        pos.x <= bounds.x + bounds.w &&
        pos.y <= bounds.y + bounds.h
      ) {
        obj[handlerName]({ x: pos.x - bounds.x, y: pos.y - bounds.y, button: e.button });
      } else {
        obj[handlerName](undefined);
      }
    });
  }

  private attachEvent() {
    ['MouseMove', 'MouseUp', 'MouseDown', 'DblClick'].forEach(
      (ev) => {
        // @ts-ignore
        this._renderer.canvas.addEventListener(ev.toLowerCase().replace('mouse', 'pointer'), this.mouseHandler.bind(this, ev));
      },
    );
    this._renderer.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
  }

  private _loadMap() {
    if (!this._stage) {
      throw new Error('No map has been selected');
    }

    this.physics.createStage(this._stage);
    this._camera.initializePosition();
  }

  public clearMarbles() {
    this.physics.clearMarbles();
    this._winner = null;
    this._winners = [];
    this._marbles = [];
    this._finishedMarbles = [];
    // _allMarbles는 순위 리스트 표시를 위해 유지 (리셋 시에만 초기화)
  }

  public start() {
    this._isRunning = true;
    this._winnerRank = options.winningRank;
    this._winnerCount = options.winnerCount;
    if (this._winnerRank >= this._marbles.length) {
      this._winnerRank = this._marbles.length - 1;
    }
    if (this._winnerCount > this._marbles.length) {
      this._winnerCount = this._marbles.length;
    }
    if (this._winnerCount < 1) {
      this._winnerCount = 1;
    }
    this._camera.startFollowingMarbles();

    if (this._autoRecording) {
      this._recorder.start().then(() => {
        this.physics.start();
        this._marbles.forEach((marble) => (marble.isActive = true));
      });
    } else {
      this.physics.start();
      this._marbles.forEach((marble) => (marble.isActive = true));
    }
  }

  public setSpeed(value: number) {
    if (value <= 0) {
      throw new Error('Speed multiplier must larger than 0');
    }
    this._speed = value;
  }

  public setTheme(themeName: keyof typeof Themes) {
    this._theme = Themes[themeName];
  }

  public getSpeed() {
    return this._speed;
  }

  public setWinningRank(rank: number) {
    this._winnerRank = rank;
  }

  public setWinnerCount(count: number) {
    this._winnerCount = count;
  }

  public setAutoRecording(value: boolean) {
    this._autoRecording = value;
  }

  public setMarbles(names: string[]) {
    this.reset();
    const arr = names.slice();

    let maxWeight = -Infinity;
    let minWeight = Infinity;

    const members = arr
      .map((nameString) => {
        const result = parseName(nameString);
        if (!result) return null;
        const { name, weight, count } = result;
        if (weight > maxWeight) maxWeight = weight;
        if (weight < minWeight) minWeight = weight;
        return { name, weight, count };
      })
      .filter((member) => !!member);

    const gap = maxWeight - minWeight;

    let totalCount = 0;
    members.forEach((member) => {
      if (member) {
        member.weight = 0.1 + (gap ? (member.weight - minWeight) / gap : 0);
        totalCount += member.count;
      }
    });

    const orders = shuffle(
      Array(totalCount)
        .fill(0)
        .map((_, i) => i),
    );
    members.forEach((member) => {
      if (member) {
        for (let j = 0; j < member.count; j++) {
          const order = orders.pop() || 0;
          const marble = new Marble(
            this.physics,
            order,
            totalCount,
            member.name,
            member.weight,
          );
          this._marbles.push(marble);
          this._allMarbles.push(marble); // 전체 구슬 목록에도 추가
        }
      }
    });
    this._totalMarbleCount = totalCount;
    
    // Slack 이미지 로드 (비동기, 에러 무시)
    const uniqueNames: string[] = [];
    members.forEach((member) => {
      if (member && !uniqueNames.includes(member.name)) {
        uniqueNames.push(member.name);
      }
    });
    if (uniqueNames.length > 0) {
      this._renderer.loadSlackImagesForNames(uniqueNames).catch((error) => {
        console.warn('Failed to load Slack images:', error);
      });
    }
  }
  
  /**
   * Slack API를 사용하여 사용자 정보를 로드합니다
   * @param apiBaseUrl 서버 API 기본 URL (선택사항, 기본값: '/api')
   */
  public async loadSlackUsers(apiBaseUrl: string = '/api'): Promise<void> {
    await this._renderer.loadSlackUsers(apiBaseUrl);
  }
  
  /**
   * 여러 이름에 대한 Slack 이미지를 일괄 로드합니다
   * @param names 구슬 이름 배열
   */
  public async loadSlackImagesForNames(names: string[]): Promise<void> {
    await this._renderer.loadSlackImagesForNames(names);
  }

  private _clearMap() {
    this.physics.clear();
    this._marbles = [];
    this._allMarbles = [];
  }

  public reset() {
    this.clearMarbles();
    this._clearMap();
    this._loadMap();
    this._goalDist = Infinity;
  }

  public getCount() {
    return this._marbles.length;
  }

  public getMaps() {
    return stages.map((stage, index) => {
      return {
        index,
        title: stage.title,
      };
    });
  }

  public setMap(index: number) {
    if (index < 0 || index > stages.length - 1) {
      throw new Error('Incorrect map number');
    }
    const names = this._marbles.map((marble) => marble.name);
    this._stage = stages[index];
    this.setMarbles(names);
    this._camera.initializePosition();
  }
}
